import { useTranslation } from 'react-i18next';
import CopyTextWrapper from 'components/others/CopyTextWrapper';
import EToolTip from 'components/others/EToolTip';
import type { ProductPreviewRow, WarehouseSourceKey } from '../model/types';

const WAREHOUSE_BADGE_CLASS: Record<'main' | 'reserve' | 'team' | 'custom', string> = {
    main: 'bg-light-info text-info',
    reserve: 'bg-light-warning text-warning',
    team: 'bg-light-primary text-primary',
    custom: 'bg-light-info text-info',
};

const getWarehouseBadgeClass = (sourceKey: WarehouseSourceKey | null | undefined): string => {
    if (!sourceKey) return '';
    if (sourceKey === 'custom' || String(sourceKey).startsWith('custom_')) {
        return WAREHOUSE_BADGE_CLASS.custom;
    }
    return WAREHOUSE_BADGE_CLASS[sourceKey as 'main' | 'reserve' | 'team'] || '';
};

interface ProductsTransferTableRowProps {
    row: ProductPreviewRow;
    mode: 'preview' | 'result';
    colProduct: string;
    colArticle: string;
    colWarehouse: string;
    colQty: string;
    statusOk: string;
    statusFail: string;
    statusPartial: string;
    onToggle?: (key: string) => void;
}

const ProductsTransferTableRow = ({
    row,
    mode,
    colProduct,
    colArticle,
    colWarehouse,
    colQty,
    statusOk,
    statusFail,
    statusPartial,
    onToggle,
}: ProductsTransferTableRowProps) => {
    const { t } = useTranslation(['Objects']);
    const ns = 'documents.proposal2Warehouse';

    const rowClass =
        mode === 'result'
            ? row.status === 'ok'
                ? 'proposal2warehouse-row-ok'
                : row.status === 'partial'
                  ? 'proposal2warehouse-row-partial'
                  : 'proposal2warehouse-row-fail'
            : row.alreadyOnObject
              ? 'proposal2warehouse-row-existing'
              : !(row.transferCount > 0)
                ? 'bg-light-danger'
                : undefined;

    const statusLabel =
        row.status === 'ok'
            ? statusOk
            : row.status === 'partial'
              ? statusPartial
              : statusFail;

    const statusBadgeClass =
        row.status === 'ok'
            ? 'bg-light-success text-success'
            : row.status === 'partial'
              ? 'bg-light-warning text-warning'
              : 'bg-light-danger text-danger';

    const article = row.prodArticle || '';
    const badgeClass = getWarehouseBadgeClass(row.sourceKey);
    const showCount = row.alreadyOnObject
        ? Number(row.existingCount || 0)
        : Number(row.transferCount || 0);

    const qtyToneClass = row.alreadyOnObject
        ? 'text-success'
        : row.availability <= 0
          ? 'text-danger'
          : row.availability < row.need
            ? 'text-warning'
            : 'text-success';

    return (
        <tr className={rowClass}>
            {mode === 'preview' ? (
                <td className="check-cell">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={row.selected}
                        disabled={row.alreadyOnObject || !(row.transferCount > 0)}
                        onChange={() => onToggle?.(row.key)}
                    />
                </td>
            ) : null}
            <td className="name" data-label={colProduct}>
                <div className="fw-medium">{row.prodName}</div>
                {row.vendorName ? (
                    <small className="text-primary">{row.vendorName}</small>
                ) : null}
            </td>
            <td className="article text-truncate" data-label={colArticle}>
                <CopyTextWrapper value={article}>
                    <span title={article}>{article || '—'}</span>
                </CopyTextWrapper>
            </td>
            <td className="warehouse" data-label={colWarehouse}>
                {row.alreadyOnObject ? (
                    <EToolTip title={t(`${ns}.badgeExisting`)}>
                        <span className="badge bg-light-success text-success">
                            {row.warehouseLabel}
                        </span>
                    </EToolTip>
                ) : row.sourceKey ? (
                    <span className={`badge ${badgeClass}`}>{row.warehouseLabel}</span>
                ) : (
                    <span className="text-muted">—</span>
                )}
            </td>
            <td className="qty text-end" data-label={colQty}>
                <div className={`fw-medium ${qtyToneClass}`}>
                    {row.need}
                    <span className="opacity-75"> / </span>
                    {row.availability}
                </div>
                {showCount > 0 ? (
                    <span className="text-muted f-12 d-block">×{showCount}</span>
                ) : null}
            </td>
            {mode === 'result' ? (
                <td className="status" data-label={statusLabel}>
                    <span className={`badge ${statusBadgeClass}`}>{statusLabel}</span>
                    {row.errorMessage ? (
                        <small className="text-danger d-block mt-1">{row.errorMessage}</small>
                    ) : null}
                </td>
            ) : null}
        </tr>
    );
};

export default ProductsTransferTableRow;
